/**
 * User-scoped localStorage utility to prevent data bleeding between users
 * All keys are prefixed with user identifier to ensure data isolation
 */

interface UserInfo {
  id?: string | number;
  email?: string;
}

class UserStorage {
  private userPrefix: string | null = null;

  /**
   * Initialize with user information
   * Should be called on login/auth state change
   */
  setUser(user: UserInfo | null) {
    if (user) {
      // Use user ID if available, otherwise use email
      const identifier = user.id || user.email || 'anonymous';
      this.userPrefix = `user_${identifier}`;
    } else {
      this.userPrefix = null;
    }
  }

  /**
   * Get current user prefix
   */
  getUserPrefix(): string | null {
    return this.userPrefix;
  }

  /**
   * Get a user-scoped key
   */
  private getScopedKey(key: string): string {
    if (!this.userPrefix) {
      console.warn('[UserStorage] No user set, using global key:', key);
      return key;
    }
    return `${this.userPrefix}:${key}`;
  }

  /**
   * Get item from user-scoped localStorage
   */
  getItem(key: string): string | null {
    const scopedKey = this.getScopedKey(key);
    return localStorage.getItem(scopedKey);
  }

  /**
   * Set item in user-scoped localStorage
   */
  setItem(key: string, value: string): void {
    const scopedKey = this.getScopedKey(key);
    localStorage.setItem(scopedKey, value);
  }

  /**
   * Remove item from user-scoped localStorage
   */
  removeItem(key: string): void {
    const scopedKey = this.getScopedKey(key);
    localStorage.removeItem(scopedKey);
  }

  /**
   * Clear all user-scoped items, EXCEPT pure UI preferences (column views,
   * saved presets, filters). Those contain no PHI and are already isolated
   * per user by the key prefix, so preserving them across logout is safe —
   * wiping them meant the 15-min idle auto-logout silently reset the user's
   * table view every time they stepped away.
   */
  clearUserData(): void {
    if (!this.userPrefix) return;

    const preserved = new Set(
      PRESERVED_PREFERENCE_KEYS.map(k => `${this.userPrefix}:${k}`)
    );

    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.userPrefix + ':') && !preserved.has(key)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  /**
   * Clear ALL localStorage data (use with caution)
   * Useful for logout to ensure complete cleanup
   */
  clearAll(): void {
    // Keep auth token if it exists (will be removed by auth logic)
    const authToken = localStorage.getItem('auth_token');
    
    // Clear everything
    localStorage.clear();
    
    // Restore auth token if needed (auth logic will handle removal)
    if (authToken) {
      localStorage.setItem('auth_token', authToken);
    }
  }

  /**
   * Migrate old non-prefixed keys to user-scoped keys
   * This helps transition existing data to the new system
   */
  migrateOldKeys(keys: string[]): void {
    if (!this.userPrefix) return;

    keys.forEach(key => {
      const oldValue = localStorage.getItem(key);
      if (oldValue !== null) {
        // Save to user-scoped key
        this.setItem(key, oldValue);
        // Remove old global key
        localStorage.removeItem(key);
        console.log(`[UserStorage] Migrated ${key} to user-scoped storage`);
      }
    });
  }

  /**
   * Clean up old non-prefixed keys that shouldn't persist
   */
  cleanupGlobalKeys(keys: string[]): void {
    keys.forEach(key => {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        console.log(`[UserStorage] Removed global key: ${key}`);
      }
    });
  }

  /**
   * Special handling for practice/client selection
   * These should use sessionStorage for better isolation
   */
  getSessionItem(key: string): string | null {
    return sessionStorage.getItem(key);
  }

  setSessionItem(key: string, value: string): void {
    sessionStorage.setItem(key, value);
  }

  clearSessionData(): void {
    sessionStorage.clear();
  }
}

// Create singleton instance
const userStorage = new UserStorage();

// UI-preference keys that SURVIVE logout (no PHI; per-user prefixed, so no
// cross-account bleed on shared browsers). Everything else user-scoped is
// still wiped on logout.
export const PRESERVED_PREFERENCE_KEYS = [
  'gaps:colPresets',
  'gaps:activePresetId',
  'gaps:colSettings',
  'gaps:filters',
  'measureDetail:colPresets',
  'measureDetail:activePresetId',
  'measureDetail:colSettings',
  'patients:filters',
  // APP Plus — QPS Management column views + active preset (owner 2026-09-12:
  // the view reset on every logout while the MIPS equivalent survived).
  'app:qps:views:v2',
];

// List of keys that should be user-scoped
export const USER_SCOPED_KEYS = [
  'gaps:colPresets',
  'gaps:activePresetId',
  'gaps:colSettings',
  'gaps:filters',
  'gaps:measureNotes',
  'gaps:measureEvidence',
  'gaps:selectedBaseIds',
  'gaps:additions',
  'measureDetail:colPresets',
  'measureDetail:activePresetId',
  'measureDetail:colSettings',
  'patients:filters',
  'app:qps:views:v2',
];

// Keys that should be in sessionStorage (cleared on browser close)
export const SESSION_KEYS = [
  'selectedPracticeId',
  'selectedClientId',
  'currentPractice',
];

// Keys that should be cleaned up on logout
export const CLEANUP_KEYS = [
  ...USER_SCOPED_KEYS,
  ...SESSION_KEYS,
  'practiceInfo',
];

export default userStorage;
